import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { useAuth } from '../App';
import { mapCognitoError } from '../utils/helpers';
import Logo from '../components/Logo';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
  },
  logo: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#01696F',
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  subtitle: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: '0.9rem',
    marginBottom: '2rem',
  },
  formGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#1e293b',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  btn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#01696F',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  resendBtn: {
    width: '100%',
    padding: '0.6rem',
    backgroundColor: 'transparent',
    color: '#01696F',
    border: '1px solid #01696F',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '0.75rem',
  },
  error: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#ef4444',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  info: {
    backgroundColor: '#f0fdfa',
    border: '1px solid #99f6e4',
    color: '#01696F',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  footer: {
    textAlign: 'center',
    marginTop: '1.5rem',
    fontSize: '0.875rem',
    color: '#64748b',
  },
  footerLink: {
    color: '#01696F',
    fontWeight: '500',
    textDecoration: 'none',
  },
};

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailFromQuery);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const intervalRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      try {
        const { autoSignIn } = await import('aws-amplify/auth');
        const result = await autoSignIn();
        const idToken = result.tokens?.idToken?.toString();
        if (idToken) {
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const tokenPayload = session.tokens?.idToken?.payload || {};
          login(idToken, { id: tokenPayload.sub, email: tokenPayload.email, name: tokenPayload.name || '' });
          navigate('/dashboard');
          return;
        }
      } catch (_) {}
      navigate('/login', { state: { flash: 'Email verified! Please sign in.' } });
    } catch (err) {
      setError(mapCognitoError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    setResendCooldown(30);
    intervalRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    try {
      await resendSignUpCode({ username: email });
      setInfo('A new code has been sent to your email.');
    } catch (err) {
      setError(mapCognitoError(err));
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}><Link to="/" style={{ textDecoration: 'none' }}><Logo size="medium" /></Link></div>
        <div style={styles.subtitle}>Verify your email</div>

        {info && <div style={styles.info}>{info}</div>}
        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Verification Code</label>
            <input
              style={styles.input}
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="Enter 6-digit code"
              required
              autoFocus
            />
          </div>
          <button
            type="submit"
            style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <button
          type="button"
          style={{ ...styles.resendBtn, ...(resendCooldown > 0 ? styles.btnDisabled : {}) }}
          onClick={handleResend}
          disabled={resendCooldown > 0}
        >
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s...` : 'Resend code'}
        </button>

        <div style={styles.footer}>
          Already verified?{' '}
          <Link to="/login" style={styles.footerLink}>Sign in</Link>
        </div>
      </div>
    </div>
  );
}
